import { applyTransform as C, invertTransform as T, composeTransform as w } from "../shared/matrix.js";
import { computeBbox as R, webMercatorToLonLat as g, bboxToSize as j, bboxToRectangle as G, sizesToScale as m, bboxToCenter as x, bufferBboxByRatio as f } from "@allmaps/stdlib";
class S {
  geoCenter;
  geoRectangle;
  geoSize;
  geoResolution;
  geoRectangleBbox;
  projectedGeoCenter;
  projectedGeoRectangle;
  projectedGeoSize;
  projectedGeoResolution;
  projectedGeoRectangleBbox;
  rotation;
  projectedGeoPerViewportScale;
  viewportCenter;
  viewportRectangle;
  viewportSize;
  viewportResolution;
  viewportBbox;
  devicePixelRatio;
  canvasCenter;
  canvasRectangle;
  canvasSize;
  canvasResolution;
  canvasBbox;
  projectedGeoPerCanvasScale;
  projectedGeoToViewportTransform = [1, 0, 0, 1, 0, 0];
  projectedGeoToClipTransform = [1, 0, 0, 1, 0, 0];
  viewportToClipTransform = [1, 0, 0, 1, 0, 0];
  /**
   * Creates a new Viewport
   *
   * @constructor
   * @param {Size} viewportSize - Size of the viewport in viewport pixels, as [width, height].
   * @param {Point} projectedGeoCenter - Center point of the viewport, in projected coordinates.
   * @param {number} projectedGeoPerViewportScale - Scale of the viewport, in projection coordinates per viewport pixel.
   * @param {number} rotation - Rotation of the viewport with respect to the project coordinate system.
   * @param {number} [devicePixelRatio=1] - The devicePixelRatio of the viewport.
   */
  constructor(e, t, o, i, s = 1) {
    this.projectedGeoCenter = t, this.projectedGeoPerViewportScale = o, this.rotation = i, this.viewportSize = e, this.devicePixelRatio = s, this.projectedGeoRectangle = this.computeProjectedGeoRectangle(
      this.projectedGeoCenter,
      this.projectedGeoPerViewportScale,
      this.rotation,
      this.viewportSize
    ), this.projectedGeoRectangleBbox = R(this.projectedGeoRectangle), this.projectedGeoSize = [
      this.viewportSize[0] * o,
      this.viewportSize[1] * o
    ], this.projectedGeoResolution = this.projectedGeoSize[0] * this.projectedGeoSize[1], this.geoCenter = g(this.projectedGeoCenter), this.geoRectangle = this.projectedGeoRectangle.map((r) => g(r)), this.geoRectangleBbox = R(this.geoRectangle), this.geoSize = j(this.geoRectangleBbox), this.geoResolution = this.geoSize[0] * this.geoSize[1], this.viewportResolution = this.viewportSize[0] * this.viewportSize[1], this.viewportCenter = [this.viewportSize[0] / 2, this.viewportSize[1] / 2], this.viewportBbox = [0, 0, ...this.viewportSize], this.viewportRectangle = G(this.viewportBbox), this.canvasCenter = [
      this.viewportCenter[0] * this.devicePixelRatio,
      this.viewportSize[1] * this.devicePixelRatio
    ], this.canvasSize = [
      this.viewportSize[0] * this.devicePixelRatio,
      this.viewportSize[1] * this.devicePixelRatio
    ], this.canvasResolution = this.canvasSize[0] * this.canvasSize[1], this.canvasBbox = [0, 0, ...this.canvasSize], this.canvasRectangle = G(this.canvasBbox), this.projectedGeoPerCanvasScale = this.projectedGeoPerViewportScale / this.devicePixelRatio, this.projectedGeoToViewportTransform = this.composeProjectedGeoToViewportTransform(), this.projectedGeoToClipTransform = this.composeProjectedGeoToClipTransform(), this.viewportToClipTransform = this.composeViewportToClipTransform();
  }
  /**
   * Static method creates that creates a Viewport from a WarpedMapList
   *
   * @static
   * @template {WarpedMap} W
   * @param {Size} viewportSize - Size of the viewport in viewport pixels, as [width, height].
   * @param {WarpedMapList<W>} warpedMapList - A WarpedMapList.
   * @param {number} [devicePixelRatio] - The devicePixelRatio of the viewport.
   * @param {Fit} [fit='contain'] - Whether the viewport should contain or cover the bbox of the warpedMapList.
   * @returns {Viewport} - A new Viewport object
   */
  static fromWarpedMapList(e, t, o, i = "contain", s = 1) {
    const r = t.getProjectedCenter(), c = t.getProjectedBbox();
    if (!r || !c)
      throw new Error("WarpedMapList has no projected center or bbox");
    const p = j(c), n = m(p, e, i) * (1 / s);
    return new S(
      e,
      r,
      n,
      0,
      o
    );
  }
  /**
   * Static method creates that creates a Viewport from Bbox in projected geospatial coordinates.
   *
   * @static
   * @template {WarpedMap} W
   * @param {Size} viewportSize - Size of the viewport in viewport pixels, as [width, height].
   * @param {WarpedMapList<W>} projectedGeoBbox - A projectedGeoBbox.
   * @param {number} [devicePixelRatio] - The devicePixelRatio of the viewport.
   * @param {Fit} [fit='contain'] - Whether the viewport should contain or cover the bbox of the warpedMapList.
   * @returns {Viewport} - A new Viewport object
   */
  static fromProjectedGeoBbox(e, t, o, i = "contain") {
    const s = x(t), r = j(t), c = m(
      r,
      e,
      i
    );
    return new S(
      e,
      s,
      c,
      0,
      o
    );
  }
  getProjectedGeoBufferedRectangle(e) {
    const t = f(
      this.viewportBbox,
      e
    );
    return G(t).map(
      (i) => C(
        T(this.projectedGeoToViewportTransform),
        i
      )
    );
  }
  composeProjectedGeoToViewportTransform() {
    return w(
      this.viewportCenter[0],
      this.viewportCenter[1],
      1 / this.projectedGeoPerViewportScale,
      -1 / this.projectedGeoPerViewportScale,
      -this.rotation,
      -this.projectedGeoCenter[0],
      -this.projectedGeoCenter[1]
    );
  }
  composeProjectedGeoToClipTransform() {
    return w(
      0,
      0,
      2 / (this.projectedGeoPerViewportScale * this.viewportSize[0]),
      2 / (this.projectedGeoPerViewportScale * this.viewportSize[1]),
      -this.rotation,
      -this.projectedGeoCenter[0],
      -this.projectedGeoCenter[1]
    );
  }
  composeViewportToClipTransform() {
    return w(
      0,
      0,
      2 / this.viewportSize[0],
      -2 / this.viewportSize[1],
      0,
      -this.viewportCenter[0],
      -this.viewportCenter[1]
    );
  }
  /** Returns a rotated rectangle in projected geo coordinates */
  computeProjectedGeoRectangle(e, t, o, i) {
    const s = t * i[0] / 2, r = t * i[1] / 2, c = Math.cos(o), p = Math.sin(o), n = s * c, a = s * p, h = r * c, d = r * p, l = e[0], v = e[1];
    return [
      [l - n + d, v - a - h],
      [l - n - d, v - a + h],
      [l + n - d, v + a + h],
      [l + n + d, v + a - h]
    ];
  }
}
export {
  S as default
};
//# sourceMappingURL=Viewport.js.map
